<template>
  <CustomModal
    :id="id"
    modalClass="modal-enterprise-features"
  >
    <template #header>
      <div class="modal-enterprise-features__header">
        <BaseHeading
          level="3"
          color="monochrome-07"
        >
          Upgrade to Enterprise
        </BaseHeading>
        <button
          type="button"
          @click="$modal.hide(id)"
        >
          <icon
            data="@icon/close.svg"
            :fill="false"
            width="var(--icon-24)"
            height="var(--icon-24)"
            color="#81889B"
            class="modal-enterprise-features__close-btn"
          />
        </button>
      </div>
    </template>

    <template #body>
      <div class="modal-enterprise-features__content">
        <div class="modal-enterprise-features__list">
          <div class="modal-enterprise-features__list-label">Enterprise</div>
          <ul class="modal-enterprise-features__list-items">
            <li
              v-for="item in values"
              :key="item.id"
              class="modal-enterprise-features__list-item"
              :class="{ active: activeItemId === item.id }"
              @click="toggleItem(item.id)"
            >
              <div
                :class="[
                  'modal-enterprise-features__item-header',
                  { 'active-title': activeItemId === item.id }
                ]"
              >
                <BaseSubHeading
                  class="modal-enterprise-features__list-item-title"
                  size="large"
                  weight="400"
                  color="monochrome-06"
                  darkColor="monochrome-06"
                >
                  <icon
                    data="@icon/chevron-with-paddings.svg"
                    width="var(--icon-24)"
                    height="var(--icon-24)"
                    class="subscription__feature-icon"
                  />
                  {{ item.label }}
                </BaseSubHeading>
              </div>
              <div
                class="modal-enterprise-features__subtitle"
                :class="{ 'is-open': activeItemId === item.id }"
              >
                <BaseSubHeading
                  class="modal-enterprise-features__list-item-subtitle"
                  size="medium"
                  weight="400"
                  color="monochrome-06"
                  darkColor="monochrome-06"
                >
                  {{ item.subTitle }}
                </BaseSubHeading>
              </div>
            </li>
          </ul>
        </div>
        <div class="modal-enterprise-features__info">
          <div class="modal-enterprise-features__info-title is-flex">
            <BaseSubHeading
              size="large"
              weight="400"
              color="monochrome-06"
              darkColor="monochrome-07"
            >
              Want to upgrade to
            </BaseSubHeading>

            <BaseSubHeading
              size="large"
              weight="600"
              color="monochrome-06"
              darkColor="monochrome-07"
            >
              Enterprise plan?
            </BaseSubHeading>
          </div>

          <div class="modal-enterprise-features__info-email">
            <BaseSubHeading
              size="large"
              weight="400"
              color="monochrome-06"
              darkColor="monochrome-07"
            >
              Email us at
            </BaseSubHeading>

            <a
              href="mailto:support@projectsimple.ai"
              class="modal-enterprise-features__info-link"
              >support@projectsimple.ai</a
            >
          </div>
        </div>
      </div>
    </template>
  </CustomModal>
</template>

<script lang="ts" setup>
import { computed } from "vue"
import { useStore } from "vuex"

import BaseSubHeading from "@/components/Base/BaseSubHeading.vue"
import CustomModal from "@/components/Modal/index.vue"

import { TypeOfSubscriptionFeatures } from "@/models"
import { Actions, Getters } from "@/store"

interface EnterpriceFeature {
  id: TypeOfSubscriptionFeatures
  label: string
  subTitle: string
}

defineProps({
  id: {
    type: String,
    default: "modal-enterprise-features"
  }
})

const store = useStore()

const activeItemId = computed(() => store.getters[Getters.SELECTED_SUBSCRIPTION_FEATURE])

const toggleItem = (itemId: string) => {
  store.dispatch(
    Actions.SELECTED_SUBSCRIPTION_FEATURE,
    activeItemId.value === itemId ? null : itemId
  )
}

const values = computed((): EnterpriceFeature[] => {
  return [
    {
      id: "aiAssistant",
      label: "AI Assistant",
      subTitle:
        "Configure your own AI to help run your projects, analyze data, and provide intelligent insights"
    },
    {
      id: "timesheets",
      label: "Timesheets, CAPeX, OPeX",
      subTitle: "Provides Time Reporting and administration"
    },
    {
      id: "jira",
      label: "Jira bi-directional sync",
      subTitle: "Connects Jira and Project Simple for data synchronization"
    },
    {
      id: "permissions",
      label: "Permissions and SSO",
      subTitle: "Setup organizational roles and permissions"
    },
    {
      id: "enterpriseApi",
      label: "Enterprise API",
      subTitle: "Access to Project Simple platform APIs"
    },
    {
      id: "audit",
      label: "Audit",
      subTitle: "Access and retention of user and system activity data"
    },
    {
      id: "training",
      label: "Training and Consulting",
      subTitle: "Professional coaching by internal staff & industry experts"
    },
    {
      id: "support",
      label: "Enterprise Support",
      subTitle: "Dedicated support for a priority response"
    }
  ]
})
</script>
